// ── SubmitWithEvidenceModal ────────────────────────────────────────────────────
// Thin wrapper: composes EvidenceCaptureModal's form helpers + state-transition
// orchestration. Handles the in_progress → submitted flow including the D-F3-4
// two-click "submit without evidence" amber-banner path.
//
// Pure evidence capture (no state transition) → use EvidenceCaptureModal directly.

import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useControlEvidence, useUploadEvidence } from '@/api/hooks/useCompliance';
import { useTransitionAssignment } from '@/features/compliance/assignments/api';
import { cn } from '@/lib/utils';
import {
  EVIDENCE_ADVISORY_COPY,
  EVIDENCE_TYPES,
  emptyDraft,
  isDraftValid,
  buildContentSummary,
} from './EvidenceCaptureModal';
import type { EvidenceDraft } from './EvidenceCaptureModal';
import type { Assignment, ProposedOutcome } from '@grc/shared';

// ── Outcome options ────────────────────────────────────────────────────────────

const OUTCOME_OPTIONS: { value: ProposedOutcome | null; label: string }[] = [
  { value: null,                    label: 'Let verifier decide' },
  { value: 'implemented',           label: "I've implemented this control" },
  { value: 'compensating_control',  label: "I've put a compensating control in place" },
  { value: 'not_applicable',        label: "This control doesn't apply to us" },
];

// ── Per-entry state for multi-entry form ──────────────────────────────────────

interface EntryDraft extends EvidenceDraft {
  id: string;
  error: string | null;
  posted: boolean;
}

function emptyEntry(): EntryDraft {
  return { id: crypto.randomUUID(), error: null, posted: false, ...emptyDraft() };
}

function isEntryValid(e: EntryDraft): boolean {
  return isDraftValid(e);
}

// ── Component ─────────────────────────────────────────────────────────────────

interface Props {
  open: boolean;
  onClose: () => void;
  assignment: Assignment;
  controlId: string;
  controlRef?: string;
  controlTitle?: string;
}

export function SubmitWithEvidenceModal({
  open,
  onClose,
  assignment,
  controlId,
  controlRef,
  controlTitle,
}: Props) {
  const { data: existingEvidence, isLoading: evidenceLoading } = useControlEvidence(controlId);
  const uploadMutation = useUploadEvidence(controlId);
  const transitionMutation = useTransitionAssignment(controlId);

  const [entries, setEntries] = useState<EntryDraft[]>([emptyEntry()]);
  const [proposedOutcome, setProposedOutcome] = useState<ProposedOutcome | null>(null);
  const [confirmWithout, setConfirmWithout] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const existingList = Array.isArray(existingEvidence) ? existingEvidence : [];
  const validEntries = entries.filter(isEntryValid);
  const hasValidEvidence = existingList.length > 0 || validEntries.length > 0;

  function updateEntry(id: string, patch: Partial<EntryDraft>) {
    setEntries((prev) => prev.map((e) => (e.id === id ? { ...e, ...patch } : e)));
  }

  async function handleSubmitWithEvidence() {
    setSubmitting(true);
    let anyFailed = false;

    for (const entry of entries) {
      if (entry.posted || !isEntryValid(entry)) continue;
      try {
        await new Promise<void>((resolve, reject) => {
          uploadMutation.mutate(
            {
              title: entry.title.trim(),
              evidenceType: entry.evidenceType,
              contentSummary: buildContentSummary(entry.location, entry.notes),
            },
            {
              onSuccess: () => { updateEntry(entry.id, { posted: true, error: null }); resolve(); },
              onError: (err: unknown) => {
                const msg =
                  (err as { response?: { data?: { errors?: { message?: string }[] } } })
                    ?.response?.data?.errors?.[0]?.message ?? 'Failed to save evidence entry';
                updateEntry(entry.id, { error: msg });
                anyFailed = true;
                reject(new Error(msg));
              },
            },
          );
        });
      } catch {
        break;
      }
    }

    if (anyFailed) {
      setSubmitting(false);
      return;
    }

    transitionMutation.mutate(
      { id: assignment.id, to: 'submitted', proposed_outcome: proposedOutcome },
      {
        onSuccess: () => {
          toast.success('Assignment submitted');
          setSubmitting(false);
          onClose();
        },
        onError: (err: unknown) => {
          const msg =
            (err as { response?: { data?: { errors?: { message?: string }[] } } })
              ?.response?.data?.errors?.[0]?.message ?? 'Transition failed';
          toast.error(msg);
          setSubmitting(false);
        },
      },
    );
  }

  // D-F3-4: two-click submit-without-evidence path — preserved verbatim
  function handleSubmitWithout() {
    if (!confirmWithout) {
      setConfirmWithout(true);
      return;
    }
    transitionMutation.mutate(
      { id: assignment.id, to: 'submitted', proposed_outcome: proposedOutcome },
      {
        onSuccess: () => { toast.success('Assignment submitted'); onClose(); },
        onError: (err: unknown) => {
          const msg =
            (err as { response?: { data?: { errors?: { message?: string }[] } } })
              ?.response?.data?.errors?.[0]?.message ?? 'Transition failed';
          toast.error(msg);
        },
      },
    );
  }

  const headerTitle = [controlRef, controlTitle].filter(Boolean).join(' — ');

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">
            Submit control — record evidence
            {headerTitle && (
              <span className="text-text-secondary font-normal ml-1">· {headerTitle}</span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Advisory copy — D-F4-4: single source, imported from EvidenceCaptureModal */}
        <div className="rounded-md border border-accent-sky/30 bg-accent-sky/5 px-3 py-2.5 text-xs text-text-primary">
          {EVIDENCE_ADVISORY_COPY}
        </div>

        {/* Proposed outcome — H-5 */}
        <div className="space-y-1.5">
          <label className="text-[10px] font-semibold text-text-secondary uppercase tracking-wider block">
            Proposed outcome
          </label>
          <select
            value={proposedOutcome ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              setProposedOutcome(v === '' ? null : v as ProposedOutcome);
            }}
            className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-1.5 text-xs text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-sky/50"
          >
            {OUTCOME_OPTIONS.map((o) => (
              <option key={o.value ?? '__null'} value={o.value ?? ''}>{o.label}</option>
            ))}
          </select>
          <p className="text-[10px] text-text-secondary">
            Optional — helps the verifier understand your recommendation. They can override.
          </p>
        </div>

        {/* Existing evidence list */}
        <div className="space-y-2">
          <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Recorded evidence</p>
          {evidenceLoading ? (
            <Skeleton className="h-12 w-full" />
          ) : existingList.length === 0 ? (
            <p className="text-xs text-text-secondary italic">No evidence recorded yet.</p>
          ) : (
            <div className="space-y-1.5">
              {existingList.map((e) => (
                <div
                  key={e.id}
                  className="rounded-md border border-border-subtle bg-bg-surface p-2.5 space-y-0.5"
                >
                  <p className="text-xs font-medium text-text-primary">{e.title}</p>
                  <div className="flex items-center gap-2">
                    <Badge variant="muted" className="text-[9px]">{e.evidence_type.replace(/_/g, ' ')}</Badge>
                    <span className="text-[10px] text-text-secondary">
                      {new Date(e.collected_at).toLocaleDateString()}
                    </span>
                    {e.content_summary && (
                      <span className="text-[10px] text-text-secondary line-clamp-1 flex-1">
                        {e.content_summary}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* New-entry forms (multi-entry — EvidenceCaptureModal helpers reused) */}
        <div className="space-y-3">
          <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider">Record evidence</p>
          {entries.map((entry, idx) => (
            <div
              key={entry.id}
              className={cn(
                'rounded-lg border p-3 space-y-2',
                entry.error ? 'border-accent-rose/40 bg-accent-rose/5' : 'border-border-subtle bg-bg-surface-2',
              )}
            >
              {entries.length > 1 && (
                <p className="text-[10px] text-text-secondary font-medium">Entry {idx + 1}</p>
              )}

              {/* Field 1: Title */}
              <div>
                <label className="text-[10px] text-text-secondary mb-1 block">
                  Evidence title <span className="text-accent-rose">*</span>
                </label>
                <input
                  type="text"
                  value={entry.title}
                  onChange={(e) => updateEntry(entry.id, { title: e.target.value })}
                  placeholder='e.g. "Access review Q1 2026" or "Firewall config export"'
                  maxLength={255}
                  className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-1.5 text-xs text-text-primary placeholder:text-text-secondary focus:outline-none focus:ring-1 focus:ring-accent-sky/50"
                />
              </div>

              {/* Field 2: Evidence type */}
              <div>
                <label className="text-[10px] text-text-secondary mb-1 block">
                  Evidence type <span className="text-accent-rose">*</span>
                </label>
                <select
                  value={entry.evidenceType}
                  onChange={(e) => updateEntry(entry.id, { evidenceType: e.target.value })}
                  className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-1.5 text-xs text-text-primary focus:outline-none"
                >
                  {EVIDENCE_TYPES.map((t) => (
                    <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                  ))}
                </select>
              </div>

              {/* Field 3: Evidence date */}
              <div>
                <label className="text-[10px] text-text-secondary mb-1 block">
                  Evidence date <span className="text-accent-rose">*</span>
                </label>
                <input
                  type="date"
                  value={entry.collectedAt}
                  onChange={(e) => updateEntry(entry.id, { collectedAt: e.target.value })}
                  max={new Date().toISOString().substring(0, 10)}
                  className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-1.5 text-xs text-text-primary focus:outline-none"
                />
              </div>

              {/* Field 4: Location / reference */}
              <div>
                <label className="text-[10px] text-text-secondary mb-1 block">Location / reference</label>
                <textarea
                  value={entry.location}
                  onChange={(e) => updateEntry(entry.id, { location: e.target.value })}
                  placeholder="SharePoint link, file path, system name, ticket ID, document reference"
                  rows={2}
                  maxLength={500}
                  className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-1.5 text-xs text-text-primary placeholder:text-text-secondary focus:outline-none resize-none"
                />
              </div>

              {/* Field 5: Notes */}
              <div>
                <label className="text-[10px] text-text-secondary mb-1 block">Notes</label>
                <textarea
                  value={entry.notes}
                  onChange={(e) => updateEntry(entry.id, { notes: e.target.value })}
                  placeholder="Any context auditors need — who produced it, scope or limitations, external party if applicable"
                  rows={3}
                  maxLength={2000}
                  className="w-full rounded-md border border-border-subtle bg-bg-surface px-3 py-1.5 text-xs text-text-primary placeholder:text-text-secondary focus:outline-none resize-none"
                />
              </div>

              {entry.error && (
                <p className="text-[10px] text-accent-rose">{entry.error}</p>
              )}
            </div>
          ))}

          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-xs"
            onClick={() => setEntries((prev) => [...prev, emptyEntry()])}
          >
            + Add another
          </Button>
        </div>

        {/* D-F3-4: amber banner appears on first click of "Submit without evidence" */}
        {confirmWithout && (
          <div className="flex items-start gap-2 rounded-md border border-accent-amber/30 bg-accent-amber/10 px-3 py-2.5">
            <AlertTriangle className="h-3.5 w-3.5 text-accent-amber shrink-0 mt-0.5" />
            <p className="text-xs text-accent-amber">
              No evidence will be attached to this submission. Continue?
            </p>
          </div>
        )}

        {/* Footer actions */}
        <div className="flex items-center justify-between gap-3 pt-1 border-t border-border-subtle">
          <Button
            variant="ghost"
            size="sm"
            className="text-xs"
            onClick={onClose}
            disabled={submitting || transitionMutation.isPending}
          >
            Cancel
          </Button>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              className="text-xs"
              onClick={handleSubmitWithout}
              disabled={submitting || transitionMutation.isPending}
            >
              {confirmWithout ? 'Confirm submit' : 'Submit without evidence'}
            </Button>
            <Button
              variant="default"
              size="sm"
              className="text-xs"
              onClick={handleSubmitWithEvidence}
              disabled={!hasValidEvidence || submitting || transitionMutation.isPending}
            >
              Submit with evidence
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
